import { useState, memo } from 'react';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { usePresenceStatus, PRESENCE_UI } from '../lib/presence';
import { useI18n } from '../lib/i18n';
import { CallFromPrompt } from './CallFromPrompt';

type CallResult = { status: 'ringing' | 'no_call_from' | 'not_configured' | 'failed'; detail?: string };

/**
 * Avatar del usuario: intenta la foto (/api/users/:id/avatar); si no hay (404) o falla,
 * cae a las iniciales. Se usa donde la gente comenta (updates, threads, kudos, perfil).
 *
 * Feedback 2026-07-30: punto de presencia de RingCentral (verde=activo/rojo=offline, se aprende
 * solo — nada hasta que esa persona haga/reciba su primera llamada) + click-to-call. El punto se
 * pinta en cualquier tamaño; el click-to-call solo se activa desde `size >= 24` (evita marcar por
 * error desde los badges chiquitos de asignación en listas).
 */
function AvatarInner({ userId, name, size = 28, noCall = false }: { userId: string | null | undefined; name: string | null | undefined; size?: number; noCall?: boolean }) {
  const [err, setErr] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [calling, setCalling] = useState(false);
  const { me } = useAuth();
  const { t } = useI18n();
  const presence = usePresenceStatus(userId);

  const initials = (name || '?').split(' ').filter(Boolean).map((s) => s[0]).slice(0, 2).join('').toUpperCase() || '?';
  const base: React.CSSProperties = { width: size, height: size, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, verticalAlign: 'middle' };

  const myId = me?.kind === 'tenant' ? me.user.id : null;
  // `noCall`: el avatar es solo identidad (selector del share screen, tarjeta de invitación,
  // cabecera del visor). Sin la prop, un avatar de 40 px en una lista de 40 personas marcaría por
  // teléfono al primer clic equivocado.
  const callable = !noCall && size >= 24 && !!userId && userId !== myId;

  async function place() {
    if (!userId || calling) return;
    setCalling(true);
    try {
      const r = await api.post<CallResult>(`/api/users/${userId}/call`, {});
      if (r.status === 'ringing') alert(t('call.colleagueRinging', { name: name || '' }));
      else if (r.status === 'no_call_from') setPromptOpen(true);
      else if (r.status === 'not_configured' && r.detail === 'no_extension_yet') alert(t('call.noExtensionYet', { name: name || '' }));
      else if (r.status !== 'not_configured') alert(t('call.failed'));
    } catch (e) {
      alert(e instanceof ApiError ? e.message : t('call.failed'));
    } finally {
      setCalling(false);
    }
  }

  const onClick = callable
    ? (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); void place(); }
    : undefined;

  const dotSize = Math.max(7, Math.round(size * 0.28));
  // 5 estados, no 2: quien se pone en "No molestar" tiene que verse rojo, no verde
  // (en prod había 6 personas en DND y todas salían en verde).
  const ui = presence ? PRESENCE_UI[presence] : null;
  const dot = ui ? (
    <span
      title={t(ui.key)}
      style={{
        position: 'absolute', bottom: -1, right: -1, width: dotSize, height: dotSize, borderRadius: '50%',
        background: ui.color,
        // --surface no existe (solo --surface-2): el anillo salía transparente. --card es el fondo real.
        border: '2px solid var(--card)',
        boxShadow: presence === 'active' || presence === 'oncall' ? `0 0 0 2px ${ui.color}40` : undefined,
      }}
    />
  ) : null;

  const img = !userId || err
    ? <span style={{ ...base, background: 'var(--accent-soft)', color: 'var(--accent)', fontSize: Math.round(size * 0.4), fontWeight: 700 }}>{initials}</span>
    : <img src={`/api/users/${userId}/avatar`} alt={name || ''} onError={() => setErr(true)} style={{ ...base, objectFit: 'cover', border: '1px solid var(--line)', background: 'var(--surface-2)' }} />;

  return (
    <span
      onClick={onClick}
      style={{ position: 'relative', display: 'inline-flex', width: size, height: size, cursor: callable ? 'pointer' : undefined, opacity: calling ? 0.6 : 1 }}
      title={callable ? (name || undefined) : undefined}
    >
      {img}
      {dot}
      {promptOpen && (
        <CallFromPrompt
          onClose={() => setPromptOpen(false)}
          onSaved={() => { setPromptOpen(false); void place(); }}
        />
      )}
    </span>
  );
}

/** Memoizado: aparece en listas de 100+ filas y sus props son primitivas. */
export const Avatar = memo(AvatarInner);
